import { useCallback, useEffect, useState } from 'react';
import { debounce } from '@mui/material/utils';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';
import salesforceEntitiesService from 'services/salesforceEntitiesService';

const SEARCH_DEBOUNCE_MS = 200;
const PINNED_ENTITY_STORAGE_KEY = 'activeRaisePanel:pinnedEntity';

const useActiveRaisePanel = () => {
	const { selectedEntity, setSelectedEntity } = useIRWorkstation();

	const [searchValue, setSearchValue] = useState('');
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [pinnedId, setPinnedId] = useState(null);

	useEffect(() => {
		const stored = localStorage.getItem(PINNED_ENTITY_STORAGE_KEY);
		if (stored) {
			const parsed = JSON.parse(stored);
			if (parsed?.id) {
				setPinnedId(parsed.id);
				if (!selectedEntity?.id) {
					setSelectedEntity(parsed);
				}
			}
		}
	}, [selectedEntity, setSelectedEntity]);

	useEffect(() => {
		let cancelled = false;
		const debouncedSearch = debounce(async () => {
			setLoading(true);
			setError(null);
			try {
				const response =
					await salesforceEntitiesService.searchEntities(searchValue);
				if (!cancelled) {
					setOptions(response?.entities || []);
				}
			} catch (err) {
				if (!cancelled) {
					setError(err?.message || 'Failed to load entities');
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		}, SEARCH_DEBOUNCE_MS);

		debouncedSearch();

		return () => {
			cancelled = true;
			debouncedSearch.clear();
		};
	}, [searchValue]);

	useEffect(() => {
		if (!selectedEntity?.id && options.length) {
			const pinnedOption = pinnedId
				? options.find((option) => option.id === pinnedId)
				: null;
			setSelectedEntity(pinnedOption || options[0]);
		}
	}, [selectedEntity, options, pinnedId, setSelectedEntity]);

	const handleSelect = useCallback(
		(option) => {
			setSelectedEntity(option);
		},
		[setSelectedEntity]
	);

	const handlePinToggle = useCallback(
		(option) => {
			if (!option?.id) return;

			if (pinnedId === option.id) {
				localStorage.removeItem(PINNED_ENTITY_STORAGE_KEY);
				setPinnedId(null);
			} else {
				localStorage.setItem(PINNED_ENTITY_STORAGE_KEY, JSON.stringify(option));
				setPinnedId(option.id);
			}
		},
		[pinnedId]
	);

	const selectedEntityId = selectedEntity?.id ?? null;
	const selectedEntityName = selectedEntity?.name ?? null;

	return {
		options,
		selectedEntity,
		selectedEntityId,
		selectedEntityName,
		searchValue,
		setSearchValue,
		loading,
		error,
		onSelect: handleSelect,
		pinnedId,
		onPinToggle: handlePinToggle,
	};
};

export default useActiveRaisePanel;
